import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { Menu, X, ChevronDown, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { modulesData, moduleGroups } from '../data/modulesData';

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [mobileSolutionsOpen, setMobileSolutionsOpen] = useState(false);
  const megaMenuTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Pages with dark/blue hero backgrounds that need white logo initially
  const darkHeroPages = [
    '/distribuidores-it', '/cloud-marketplace', '/soluciones-avanzadas', '/msps',
    '/telcos', '/automatizacion-contratos', '/contacto', '/sobre-nosotros',
    '/privacidad', '/terminos', '/apm', '/modulos', '/soporte',
  ];
  const isOnDarkHeroPage =
    darkHeroPages.includes(location.pathname) ||
    location.pathname.startsWith('/modulos/');
  const isHomePage = location.pathname === '/';
  const showWhiteLogo = !isScrolled && (isHomePage || isOnDarkHeroPage);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menus on navigation
  useEffect(() => {
    setIsMegaMenuOpen(false);
    setIsMobileMenuOpen(false);
    setMobileSolutionsOpen(false);
  }, [location.pathname]);

  const openMega = () => {
    if (megaMenuTimer.current) clearTimeout(megaMenuTimer.current);
    setIsMegaMenuOpen(true);
  };
  const closeMega = () => {
    megaMenuTimer.current = setTimeout(() => setIsMegaMenuOpen(false), 150);
  };

  const handleAnchorClick = (e, href) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/' + href);
    } else {
      const element = document.querySelector(href);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
    setIsMobileMenuOpen(false);
  };

  const linkClass = `text-sm font-medium transition-colors hover:text-[#4E95D9] ${
    showWhiteLogo ? 'text-white/90' : 'text-gray-700'
  }`;

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-lg' : 'bg-transparent'
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <img
              src={
                showWhiteLogo
                  ? 'https://customer-assets.emergentagent.com/job_asisant-portal/artifacts/sagigh38_logo_w.png'
                  : 'https://customer-assets.emergentagent.com/job_asisant-portal/artifacts/zw52s0v6_logo_color.png'
              }
              alt="Asisant"
              className="h-12 transition-all"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-8">
            {/* Soluciones mega-menu */}
            <div
              className="relative"
              onMouseEnter={openMega}
              onMouseLeave={closeMega}
              data-testid="solutions-menu-trigger"
            >
              <button
                className={`flex items-center gap-1 ${linkClass} ${
                  location.pathname.startsWith('/modulos') ? 'text-[#4E95D9]' : ''
                }`}
                onClick={() => setIsMegaMenuOpen(!isMegaMenuOpen)}
              >
                Soluciones
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    isMegaMenuOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
            </div>

            <a
              href="#features"
              onClick={(e) => handleAnchorClick(e, '#features')}
              className={`cursor-pointer ${linkClass}`}
              data-testid="nav-features"
            >
              Características
            </a>
            <Link
              to="/soluciones-avanzadas"
              className={`${linkClass} ${
                location.pathname === '/soluciones-avanzadas' ? 'text-[#4E95D9]' : ''
              }`}
              data-testid="nav-advanced"
            >
              Soluciones Avanzadas
            </Link>
            <Link
              to="/sobre-nosotros"
              className={`${linkClass} ${
                location.pathname === '/sobre-nosotros' ? 'text-[#4E95D9]' : ''
              }`}
              data-testid="nav-about"
            >
              Sobre nosotros
            </Link>
            <Link
              to="/soporte"
              className={`${linkClass} ${
                location.pathname === '/soporte' ? 'text-[#4E95D9]' : ''
              }`}
              data-testid="nav-support"
            >
              Soporte
            </Link>
            <a
              href="#faq"
              onClick={(e) => handleAnchorClick(e, '#faq')}
              className={`cursor-pointer ${linkClass}`}
              data-testid="nav-faq"
            >
              FAQ
            </a>
          </div>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4">
            <Link to="/contacto">
              <Button
                className="text-white font-semibold px-6 py-2 rounded-full transition-all hover:scale-105 hover:shadow-lg"
                style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}
                data-testid="navbar-cta"
              >
                Comenzar
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? (
              <X className={showWhiteLogo ? 'text-white' : 'text-gray-900'} size={24} />
            ) : (
              <Menu className={showWhiteLogo ? 'text-white' : 'text-gray-900'} size={24} />
            )}
          </button>
        </div>
      </div>

      {/* MEGA MENU - Desktop */}
      {isMegaMenuOpen && (
        <div
          className="hidden lg:block absolute left-0 right-0 top-20 bg-white shadow-2xl border-t border-[#e3e4e6] animate-in slide-in-from-top-2"
          onMouseEnter={openMega}
          onMouseLeave={closeMega}
          data-testid="solutions-mega-menu"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-2xl font-bold text-gray-900">Módulos de la plataforma</h3>
                <p className="text-sm text-gray-500">Activa solo los que necesitas, escala a tu ritmo</p>
              </div>
              <Link
                to="/modulos"
                className="inline-flex items-center gap-2 text-sm font-medium text-[#2B6FA8] hover:text-[#4E95D9]"
                data-testid="view-all-modules-link"
              >
                Ver todos los módulos
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid grid-cols-3 xl:grid-cols-4 gap-8">
              {moduleGroups.map((group) => {
                const items = modulesData.filter((m) => m.group === group.name);
                return (
                  <div key={group.name}>
                    <h4 className="text-xs font-bold uppercase tracking-wider text-[#2B6FA8] mb-3">
                      {group.name}
                    </h4>
                    <ul className="space-y-2">
                      {items.map((m) => {
                        const Icon = LucideIcons[m.icon] || LucideIcons.Box;
                        return (
                          <li key={m.slug}>
                            <Link
                              to={`/modulos/${m.slug}`}
                              className="flex items-start gap-2 group py-1"
                              data-testid={`mega-menu-item-${m.slug}`}
                            >
                              <Icon className="w-4 h-4 text-[#4E95D9] mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-gray-700 group-hover:text-[#2B6FA8] group-hover:font-medium transition-all">
                                {m.name}
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* MOBILE MENU */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white rounded-2xl shadow-xl mt-2 mx-4 p-6 animate-in slide-in-from-top-2 max-h-[calc(100vh-6rem)] overflow-y-auto">
          <div className="flex flex-col gap-4">
            {/* Mobile Soluciones dropdown */}
            <div>
              <button
                onClick={() => setMobileSolutionsOpen(!mobileSolutionsOpen)}
                className="flex items-center justify-between w-full text-gray-900 font-semibold py-2"
                data-testid="mobile-solutions-toggle"
              >
                Soluciones
                <ChevronDown
                  className={`w-4 h-4 transition-transform ${
                    mobileSolutionsOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>
              {mobileSolutionsOpen && (
                <div className="pl-4 mt-2 space-y-4 border-l-2 border-[#4E95D9]/30">
                  {moduleGroups.map((group) => {
                    const items = modulesData.filter((m) => m.group === group.name);
                    return (
                      <div key={group.name}>
                        <p className="text-xs font-bold uppercase tracking-wider text-[#2B6FA8] mb-2">
                          {group.name}
                        </p>
                        <ul className="space-y-1">
                          {items.map((m) => (
                            <li key={m.slug}>
                              <Link
                                to={`/modulos/${m.slug}`}
                                className="text-sm text-gray-700 hover:text-[#4E95D9] block py-1"
                                onClick={() => setIsMobileMenuOpen(false)}
                              >
                                {m.name}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    );
                  })}
                  <Link
                    to="/modulos"
                    className="text-sm text-[#2B6FA8] font-medium hover:underline"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Ver todos los módulos →
                  </Link>
                </div>
              )}
            </div>

            <a
              href="#features"
              onClick={(e) => handleAnchorClick(e, '#features')}
              className="text-gray-700 font-medium hover:text-[#4E95D9] transition-colors cursor-pointer"
            >
              Características
            </a>
            <Link
              to="/soluciones-avanzadas"
              className="text-gray-700 font-medium hover:text-[#4E95D9] transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Soluciones Avanzadas
            </Link>
            <Link
              to="/sobre-nosotros"
              className="text-gray-700 font-medium hover:text-[#4E95D9] transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Sobre nosotros
            </Link>
            <Link
              to="/soporte"
              className="text-gray-700 font-medium hover:text-[#4E95D9] transition-colors"
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="nav-support-mobile"
            >
              Soporte
            </Link>
            <a
              href="#faq"
              onClick={(e) => handleAnchorClick(e, '#faq')}
              className="text-gray-700 font-medium hover:text-[#4E95D9] transition-colors cursor-pointer"
            >
              FAQ
            </a>

            <hr className="my-2" />
            <Link to="/contacto" onClick={() => setIsMobileMenuOpen(false)}>
              <Button
                className="text-white font-semibold w-full rounded-full"
                style={{ background: 'linear-gradient(135deg, #4E95D9, #2B6FA8)' }}
              >
                Comenzar
              </Button>
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
